/**
 * New Game — the setup page in create mode.
 *
 * Collects title, visibility, map, seats and rules on the shared
 * GameSetupPage (also the lobby's Edit Game), then POSTs them in one go.
 */

import React, { useState } from 'react'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useAuth } from '../../../hooks/useAuth'
import { useCreateGame } from '../../../hooks/useGames'
import { useMaps, useMap } from '../../../hooks/useMaps'
import { useToast } from '../../../components/ui/Toast'
import { mapPickerOptions } from '../../../lib/mapPickerOptions'
import { useReopenOnFocus } from '../../../hooks/useReopenOnFocus'
import { GameSetupPage } from '../../../components/game/setup/GameSetupPage'
import type { GameSetupDraft } from '../../../lib/gameSetupDraft'
import { DEFAULT_BOOT_AFTER_SECONDS, PLAYER_SEATS_DEFAULT } from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import { goBackOrHome } from '../../../lib/goBack'
import { resolveInitialMapId } from '../../../lib/deepLinkHelpers'


const DEFAULT_CONFIG: GameSetupDraft['config'] = {
  startingTroops: 4,
  autoAssignTerritories: true,
  maxAttacksPerTurn: null,
  maxFortifiesPerTurn: 5,
  eliminatePlayerBonus: 5,
  cardBonusSchedule: 'cycle',
}

export default function NewGameScreen() {
  const router = useRouter()
  const { user } = useAuth()
  const createGame = useCreateGame()
  const { showToast } = useToast()
  const { data: mapSummaries } = useMaps()
  const { mapId: queryMapId } = useLocalSearchParams<{ mapId?: string }>()

  const availableMaps = React.useMemo(() => mapPickerOptions(mapSummaries), [mapSummaries])

  // Pre-select the map when arriving from the gallery or map details.
  // resolveInitialMapId validates the param against the loaded map list so
  // an unrecognised or missing param safely falls back to 'carpathia'.
  const initialMapId = React.useMemo(
    () => resolveInitialMapId(queryMapId, availableMaps.map((m) => m.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // availableMaps identity is stable once loaded; queryMapId is set at
    // mount and should not change during the screen's lifetime.
    [queryMapId, mapSummaries],
  )

  // Seats default to 4 — the size most games actually get played at. 6 was the
  // ceiling masquerading as a default: it seats a game nobody fills, and the
  // host had to step it DOWN on almost every create. The page snaps it into
  // the chosen map's range.
  const [draft, setDraft] = useState<GameSetupDraft>(() => ({
    name: '',
    isPublic: true,
    maxPlayers: PLAYER_SEATS_DEFAULT,
    autoStart: true,
    bootAfterSeconds: DEFAULT_BOOT_AFTER_SECONDS,
    mapId: initialMapId,
    config: DEFAULT_CONFIG,
  }))
  const [valid, setValid] = useState(true)
  // A team game's refusal (format, bots) shows inline above Create.
  const [error, setError] = useState<string | null>(null)
  const { data: selectedMap } = useMap(draft.mapId)
  const onDraftChange = React.useCallback((next: GameSetupDraft) => { setDraft(next); setError(null) }, [])

  // Controlled picker — closed before navigating to map details (native modal
  // + expo-router don't stack well), then re-opened on return.
  const [mapPickerOpen, setMapPickerOpen] = useState(false)
  const reopenMapPicker = useReopenOnFocus(setMapPickerOpen)

  const canCreate = !!draft.name.trim() && valid

  async function handleCreate() {
    if (!canCreate) return
    const teams = teamsConfigOf(draft.config)
    setError(null)
    try {
      const game = await createGame.mutateAsync({
        name: draft.name.trim(),
        isPublic: draft.isPublic,
        mapId: draft.mapId,
        config: draft.config,
        maxPlayers: draft.maxPlayers,
        // Team games always start when every seat is filled.
        autoStart: teams ? true : draft.autoStart,
        bootAfterSeconds: draft.bootAfterSeconds,
      })
      router.replace(`/(app)/games/${game.id}`)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create game'
      if (teams) setError(message)
      else showToast(message, 'error')
    }
  }

  return (
    <GameSetupPage
      mode="create"
      heading="New Game"
      backLabel="Go back"
      onBack={() => goBackOrHome(router)}
      draft={draft}
      onChange={onDraftChange}
      error={error}
      map={selectedMap}
      maps={availableMaps}
      titlePlaceholder={`${user?.displayName ?? 'My'}'s game`}
      titleMaxLength={100}
      mapPicker={{
        open: mapPickerOpen,
        onOpenChange: setMapPickerOpen,
        onInspectMap: (mapId) => {
          reopenMapPicker()
          setMapPickerOpen(false)
          router.push(`/(app)/maps/${mapId}`)
        },
      }}
      onValidityChange={setValid}
      primary={{ label: 'Create Game', onPress: () => { void handleCreate() }, loading: createGame.isPending, disabled: !canCreate }}
    />
  )
}
